import { useState, useEffect, useMemo } from 'react';
import api from '../../utils/api';
import ShopDetailModal from '../layout/ShopDetailModal';
import MeetingsOverview from '../layout/MeetingsOverview';
import Rent from '../sections/Rent';
import IssuesBonds from '../sections/IssuesBonds';

// Group shop codes by block letter (A, B, C…)
function groupByBlock(shops) {
  return shops.reduce((acc, s) => {
    const block = s.shop_code.charAt(0);
    if (!acc[block]) acc[block] = [];
    acc[block].push(s);
    return acc;
  }, {});
}

const BLOCK_COLORS = {
  E: { bg: '#dbeafe', color: '#1d4ed8' },
  F: { bg: '#dcfce7', color: '#16a34a' },
  R: { bg: '#fef3c7', color: '#d97706' },
  V: { bg: '#fce7f3', color: '#db2777' },
};

function RentTab({ shops }) {
  const [selectedShop, setSelectedShop] = useState(null);
  const [member, setMember] = useState(null);

  useEffect(() => {
    if (!selectedShop) return;
    api.get(`/members/shop/${selectedShop.id}`)
      .then(res => setMember(res.data))
      .catch(() => setMember(null));
  }, [selectedShop]);

  if (!selectedShop) {
    return (
      <div>
        <h2 style={{ marginBottom: 20 }}>Select Shop for Rent Payments</h2>
        <select
          className="form-select"
          onChange={e => {
            const shop = shops.find(s => s.id === Number(e.target.value));
            setSelectedShop(shop || null);
            setMember(null);
          }}
        >
          <option value="">— Select Shop —</option>
          {shops.map(shop => (
            <option key={shop.id} value={shop.id}>{shop.shop_code}</option>
          ))}
        </select>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <select
          className="form-select"
          style={{ maxWidth: 220 }}
          value={selectedShop.id}
          onChange={e => {
            const shop = shops.find(s => s.id === Number(e.target.value));
            setSelectedShop(shop || null);
            setMember(null);
          }}
        >
          {shops.map(shop => (
            <option key={shop.id} value={shop.id}>{shop.shop_code}</option>
          ))}
        </select>
      </div>
      <Rent member={member} shops={shops} />
    </div>
  );
}

function IssuesBondsTab({ shops }) {
  const [selectedShop, setSelectedShop] = useState(null);
  const [member, setMember] = useState(null);

  useEffect(() => {
    if (!selectedShop) return;
    api.get(`/members/shop/${selectedShop.id}`)
      .then(res => setMember(res.data))
      .catch(() => setMember(null));
  }, [selectedShop]);

  if (!selectedShop) {
    return (
      <div>
        <h2 style={{ marginBottom: 20 }}>Select Shop for Issue Bonds</h2>
        <select
          className="form-select"
          onChange={e => {
            const shop = shops.find(s => s.id === Number(e.target.value));
            setSelectedShop(shop || null);
            setMember(null);
          }}
        >
          <option value="">— Select Shop —</option>
          {shops.map(shop => (
            <option key={shop.id} value={shop.id}>{shop.shop_code}</option>
          ))}
        </select>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <select
          className="form-select"
          style={{ maxWidth: 220 }}
          value={selectedShop.id}
          onChange={e => {
            const shop = shops.find(s => s.id === Number(e.target.value));
            setSelectedShop(shop || null);
            setMember(null);
          }}
        >
          {shops.map(shop => (
            <option key={shop.id} value={shop.id}>{shop.shop_code}</option>
          ))}
        </select>
      </div>
      <IssuesBonds member={member} shops={shops} />
    </div>
  );
}

export default function ShopsOverview({ shops, onEditShop }) {
  const [search, setSearch]         = useState('');
  const [filterBlock, setFilterBlock] = useState('');
  const [filterStatus, setFilterStatus] = useState(''); // 'filled' | 'empty' | ''
  const [memberMap, setMemberMap]   = useState({});  // shopId -> member summary
  const [loadingMap, setLoadingMap] = useState(true);
  const [selectedShop, setSelected] = useState(null);
  const [sortCol, setSortCol]       = useState('code');
  const [sortDir, setSortDir]       = useState('asc');
  const [activeTab, setActiveTab]   = useState('shops'); // 'shops' | 'meetings' | 'staff' | 'vehicles' | 'items' | 'rent' | 'bonds'
  const [staffSearch, setStaffSearch] = useState('');
  const [vehicleSearch, setVehicleSearch] = useState('');
  const [itemCategory, setItemCategory] = useState('');
  const [staffResults, setStaffResults] = useState([]);
  const [vehicleResults, setVehicleResults] = useState([]);
  const [itemResults, setItemResults] = useState([]);
  const [loadingAdvanced, setLoadingAdvanced] = useState(false);

  // Load brief member info for all shops at once
  useEffect(() => {
    if (!shops.length) return;
    setLoadingMap(true);

    Promise.all(
      shops.map(s =>
        api.get(`/members/shop/${s.id}`)
          .then(r => ({ shopId: s.id, member: r.data }))
          .catch(() => ({ shopId: s.id, member: null }))
      )
    ).then(results => {
      const map = {};
      results.forEach(({ shopId, member }) => { map[shopId] = member; });
      setMemberMap(map);
    }).finally(() => setLoadingMap(false));
  }, [shops]);

  // Advanced search: Staff
  useEffect(() => {
    if (!staffSearch.trim()) { setStaffResults([]); return; }
    setLoadingAdvanced(true);
    api.get(`/employees/search/global?name=${encodeURIComponent(staffSearch)}`)
      .then(r => setStaffResults(r.data))
      .catch(() => setStaffResults([]))
      .finally(() => setLoadingAdvanced(false));
  }, [staffSearch]);

  // Advanced search: Vehicles
  useEffect(() => {
    if (!vehicleSearch.trim()) { setVehicleResults([]); return; }
    setLoadingAdvanced(true);
    api.get(`/vehicles/search/global?number=${encodeURIComponent(vehicleSearch)}`)
      .then(r => setVehicleResults(r.data))
      .catch(() => setVehicleResults([]))
      .finally(() => setLoadingAdvanced(false));
  }, [vehicleSearch]);

  // Advanced search: Items by category
  useEffect(() => {
    if (!itemCategory) { setItemResults([]); return; }
    setLoadingAdvanced(true);
    api.get(`/items/search/global?category=${encodeURIComponent(itemCategory)}`)
      .then(r => setItemResults(r.data))
      .catch(() => setItemResults([]))
      .finally(() => setLoadingAdvanced(false));
  }, [itemCategory]);

  const blocks = useMemo(() => [...new Set(shops.map(s => s.shop_code.charAt(0)))].sort(), [shops]);

  const filtered = useMemo(() => {
    let list = [...shops];

    if (search.trim()) {
      const q = search.trim().toLowerCase();
      const normalizedQ = q.replace(/-/g, '');
      list = list.filter(s => {
        const m = memberMap[s.id];
        const normalizedCode = s.shop_code.toLowerCase().replace(/-/g, '');
        return (
          s.shop_code.toLowerCase().includes(q) ||
          normalizedCode.includes(normalizedQ) ||
          m?.name?.toLowerCase().includes(q) ||
          m?.member_no?.toLowerCase().includes(q) ||
          m?.market_name?.toLowerCase().includes(q) ||
          m?.member_handphone?.includes(q) ||
          m?.id_card_no?.toLowerCase().includes(q)
        );
      });
    }

    if (filterBlock) list = list.filter(s => s.shop_code.startsWith(filterBlock));

    if (filterStatus === 'filled') list = list.filter(s => !!memberMap[s.id]);
    if (filterStatus === 'empty')  list = list.filter(s => !memberMap[s.id]);

    // Sort
    list.sort((a, b) => {
      let va, vb;
      if (sortCol === 'code') {
        va = a.shop_code; vb = b.shop_code;
      } else if (sortCol === 'name') {
        va = memberMap[a.id]?.name || ''; vb = memberMap[b.id]?.name || '';
      } else if (sortCol === 'member_no') {
        va = memberMap[a.id]?.member_no || ''; vb = memberMap[b.id]?.member_no || '';
      }
      const cmp = String(va).localeCompare(String(vb));
      return sortDir === 'asc' ? cmp : -cmp;
    });

    return list;
  }, [shops, search, filterBlock, filterStatus, memberMap, sortCol, sortDir]);

  const toggleSort = col => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };

  const SortIcon = ({ col }) => {
    if (sortCol !== col) return <span style={{ opacity: .3, marginLeft: 4 }}>↕</span>;
    return <span style={{ marginLeft: 4 }}>{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  return (
    <div style={{ padding: '28px 32px', height: '100%', display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* ── Page header ── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 4 }}>All Shops</h1>
          <p style={{ color: 'var(--text3)', fontSize: 13 }}>
            {shops.length} total · {loadingMap ? '…' : shops.filter(s => memberMap[s.id]).length} with records
          </p>
        </div>

        {/* Tab buttons */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            className={activeTab === 'shops' ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}
            onClick={() => setActiveTab('shops')}
          >
            Shops
          </button>
          <button
            className={activeTab === 'meetings' ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}
            onClick={() => setActiveTab('meetings')}
          >
            Meetings
          </button>
          <button
            className={activeTab === 'staff' ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}
            onClick={() => setActiveTab('staff')}
          >
            Staff Search
          </button>
          <button
            className={activeTab === 'vehicles' ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}
            onClick={() => setActiveTab('vehicles')}
          >
            Vehicles
          </button>
          <button
            className={activeTab === 'items' ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}
            onClick={() => setActiveTab('items')}
          >
            Selling Items
          </button>
          <button
            className={activeTab === 'rent' ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}
            onClick={() => setActiveTab('rent')}
          >
            Rent
          </button>
          <button
            className={activeTab === 'bonds' ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}
            onClick={() => setActiveTab('bonds')}
          >
            Issue Bonds
          </button>
        </div>
      </div>

      {/* ── SHOPS TAB ── */}
      {activeTab === 'shops' && (
        <>
          {/* Block stat badges */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {blocks.sort().map(b => {
              const col = BLOCK_COLORS[b] || { bg: '#f3f4f6', color: '#374151' };
              const count = shops.filter(s => s.shop_code.startsWith(b)).length;
              const filled = shops.filter(s => s.shop_code.startsWith(b) && memberMap[s.id]).length;
              return (
                <div key={b} style={{
                  background: col.bg, color: col.color,
                  borderRadius: 8, padding: '6px 14px', fontSize: 12, fontWeight: 600,
                  cursor: 'pointer', border: `2px solid ${filterBlock === b ? col.color : 'transparent'}`,
                  transition: 'border-color .15s',
                }}
                  onClick={() => setFilterBlock(p => p === b ? '' : b)}
                >
                  Block {b} · {filled}/{count}
                </div>
              );
            })}
          </div>

          {/* Filters row */}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            {/* Search */}
            <div style={{ position: 'relative', flex: '1', minWidth: 220 }}>
              <svg width="15" height="15" fill="none" stroke="var(--text3)" strokeWidth="2" strokeLinecap="round"
                style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                <circle cx="6" cy="6" r="5"/><path d="M11 11l3 3"/>
              </svg>
              <input
                className="form-input"
                style={{ paddingLeft: 32 }}
                placeholder="Search by shop code (A11 or A-1-1), name, phone…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>

            {/* Block filter */}
            <select className="form-select" style={{ width: 140 }} value={filterBlock} onChange={e => setFilterBlock(e.target.value)}>
              <option value="">All Blocks</option>
              {blocks.sort().map(b => <option key={b} value={b}>Block {b}</option>)}
            </select>

            {/* Status filter */}
            <select className="form-select" style={{ width: 150 }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
              <option value="">All Status</option>
              <option value="filled">With Records</option>
              <option value="empty">No Records</option>
            </select>

            {/* Clear */}
            {(search || filterBlock || filterStatus) && (
              <button className="btn btn-ghost btn-sm"
                onClick={() => { setSearch(''); setFilterBlock(''); setFilterStatus(''); }}>
                ✕ Clear
              </button>
            )}

            <span style={{ color: 'var(--text3)', fontSize: 13, marginLeft: 'auto' }}>
              {filtered.length} result{filtered.length !== 1 ? 's' : ''}
            </span>
          </div>

          {/* Table */}
          <div className="card" style={{ overflow: 'hidden', flex: 1, display: 'flex', flexDirection: 'column' }}>
            <div style={{ overflowY: 'auto', flex: 1 }}>
              <table style={{ width: '100%' }}>
                <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                  <tr>
                    <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('code')}>
                      Shop Code <SortIcon col="code" />
                    </th>
                    <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('member_no')}>
                      Member No <SortIcon col="member_no" />
                    </th>
                    <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('name')}>
                      Name <SortIcon col="name" />
                    </th>
                    <th>Market Name</th>
                    <th>Phone</th>
                    <th>ID Card</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr>
                      <td colSpan={7} style={{ textAlign: 'center', padding: '48px 24px', color: 'var(--text3)', fontStyle: 'italic' }}>
                        {loadingMap ? 'Loading shop data…' : 'No shops match your search.'}
                      </td>
                    </tr>
                  ) : filtered.map(shop => {
                    const m = memberMap[shop.id];
                    const block = shop.shop_code.charAt(0);
                    const col = BLOCK_COLORS[block] || { bg: '#f3f4f6', color: '#374151' };
                    return (
                      <tr
                        key={shop.id}
                        onClick={() => setSelected(shop)}
                        style={{ cursor: 'pointer' }}
                      >
                        <td>
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                            fontWeight: 600, fontSize: 13,
                          }}>
                            <span style={{
                              width: 24, height: 24, borderRadius: 6,
                              background: col.bg, color: col.color,
                              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: 11, fontWeight: 700,
                            }}>
                              {block}
                            </span>
                            {shop.shop_code}
                          </span>
                        </td>
                        <td style={{ color: 'var(--text2)', fontFamily: 'DM Mono, monospace', fontSize: 12 }}>
                          {m?.member_no || <span style={{ color: 'var(--text3)', fontStyle: 'italic', fontFamily: 'inherit' }}>—</span>}
                        </td>
                        <td style={{ fontWeight: m?.name ? 500 : 400 }}>
                          {m?.name || <span style={{ color: 'var(--text3)', fontStyle: 'italic' }}>Not set</span>}
                        </td>
                        <td style={{ color: 'var(--text2)' }}>{m?.market_name || '—'}</td>
                        <td style={{ color: 'var(--text2)', fontSize: 12 }}>{m?.member_handphone || '—'}</td>
                        <td style={{ color: 'var(--text2)', fontSize: 12, fontFamily: 'DM Mono, monospace' }}>{m?.id_card_no || '—'}</td>
                        <td>
                          {loadingMap
                            ? <span style={{ color: 'var(--text3)', fontSize: 12 }}>…</span>
                            : m
                              ? <span className="badge badge-green">Recorded</span>
                              : <span className="badge" style={{ background: '#f3f4f6', color: '#9ca3af' }}>Empty</span>
                          }
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* ── MEETINGS TAB ── */}
      {activeTab === 'meetings' && (
        <MeetingsOverview shops={shops} members={memberMap} />
      )}

      {/* ── STAFF SEARCH TAB ── */}
      {activeTab === 'staff' && (
        <>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <input
              className="form-input"
              style={{ flex: 1, minWidth: 250 }}
              placeholder="Search staff by name…"
              value={staffSearch}
              onChange={e => setStaffSearch(e.target.value)}
            />
            {staffSearch && (
              <button className="btn btn-ghost btn-sm" onClick={() => setStaffSearch('')}>✕ Clear</button>
            )}
          </div>
          <div className="card" style={{ overflow: 'hidden', flex: 1, display: 'flex', flexDirection: 'column' }}>
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {loadingAdvanced ? (
                <p style={{ textAlign: 'center', padding: '32px', color: 'var(--text3)' }}>Loading…</p>
              ) : staffResults.length === 0 ? (
                <p style={{ textAlign: 'center', padding: '32px', color: 'var(--text3)' }}>
                  {staffSearch ? 'No staff found.' : 'Enter a name to search.'}
                </p>
              ) : (
                <table style={{ width: '100%' }}>
                  <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                    <tr>
                      <th>Photo</th>
                      <th>Name</th>
                      <th>Designation</th>
                      <th>Shop</th>
                      <th>Phone</th>
                      <th>ID No</th>
                    </tr>
                  </thead>
                  <tbody>
                    {staffResults.map(emp => (
                      <tr key={emp.id}>
                        <td style={{ padding: '8px 14px' }}>
                          {emp.photo_path ? (
                            <img src={`https://ems.ourschool.lk/uploads/${emp.photo_path}`} alt={emp.name}
                              style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover' }} />
                          ) : (
                            <div style={{
                              width: 32, height: 32, borderRadius: '50%',
                              background: 'var(--surface2)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                              color: 'var(--text3)', fontSize: 11, fontWeight: 600
                            }}>
                              {emp.name?.[0]?.toUpperCase()}
                            </div>
                          )}
                        </td>
                        <td style={{ fontWeight: 500 }}>{emp.name}</td>
                        <td><span className="badge badge-blue">{emp.job_type}</span></td>
                        <td style={{ fontFamily: 'DM Mono, monospace', fontSize: 12 }}>{emp.shop_code}</td>
                        <td style={{ fontSize: 12 }}>{emp.phone || '—'}</td>
                        <td style={{ fontSize: 12, fontFamily: 'DM Mono, monospace' }}>{emp.id_no || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </>
      )}

      {/* ── VEHICLES SEARCH TAB ── */}
      {activeTab === 'vehicles' && (
        <>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <input
              className="form-input"
              style={{ flex: 1, minWidth: 250 }}
              placeholder="Search vehicles by number…"
              value={vehicleSearch}
              onChange={e => setVehicleSearch(e.target.value)}
            />
            {vehicleSearch && (
              <button className="btn btn-ghost btn-sm" onClick={() => setVehicleSearch('')}>✕ Clear</button>
            )}
          </div>
          <div className="card" style={{ overflow: 'hidden', flex: 1, display: 'flex', flexDirection: 'column' }}>
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {loadingAdvanced ? (
                <p style={{ textAlign: 'center', padding: '32px', color: 'var(--text3)' }}>Loading…</p>
              ) : vehicleResults.length === 0 ? (
                <p style={{ textAlign: 'center', padding: '32px', color: 'var(--text3)' }}>
                  {vehicleSearch ? 'No vehicles found.' : 'Enter a vehicle number to search.'}
                </p>
              ) : (
                <table style={{ width: '100%' }}>
                  <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                    <tr>
                      <th>Type</th>
                      <th>Number</th>
                      <th>Owner</th>
                      <th>Driver</th>
                      <th>Shop</th>
                      <th>Driver ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vehicleResults.map(veh => (
                      <tr key={veh.id}>
                        <td><span className="badge badge-yellow" style={{ textTransform: 'capitalize' }}>{veh.type}</span></td>
                        <td style={{ fontFamily: 'DM Mono, monospace', fontSize: 12 }}>{veh.number || '—'}</td>
                        <td>{veh.owner || '—'}</td>
                        <td style={{ fontWeight: 500 }}>{veh.driver || '—'}</td>
                        <td style={{ fontFamily: 'DM Mono, monospace', fontSize: 12 }}>{veh.shop_code}</td>
                        <td style={{ fontSize: 12 }}>{veh.driver_id_no || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </>
      )}

      {/* ── SELLING ITEMS TAB ── */}
      {activeTab === 'items' && (
        <>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <select className="form-select" style={{ flex: 1, maxWidth: 300 }} value={itemCategory} onChange={e => setItemCategory(e.target.value)}>
              <option value="">Select a category…</option>
              <option value="vegetables">Vegetables</option>
              <option value="fruits">Fruits</option>
              <option value="potato_onion">Potato & Onion (Import)</option>
              <option value="other">Other</option>
            </select>
            {itemCategory && (
              <button className="btn btn-ghost btn-sm" onClick={() => setItemCategory('')}>✕ Clear</button>
            )}
          </div>
          <div className="card" style={{ overflow: 'hidden', flex: 1, display: 'flex', flexDirection: 'column' }}>
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {loadingAdvanced ? (
                <p style={{ textAlign: 'center', padding: '32px', color: 'var(--text3)' }}>Loading…</p>
              ) : itemResults.length === 0 ? (
                <p style={{ textAlign: 'center', padding: '32px', color: 'var(--text3)' }}>
                  {itemCategory ? 'No shops selling this category.' : 'Select a category to view.'}
                </p>
              ) : (
                <table style={{ width: '100%' }}>
                  <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                    <tr>
                      <th>Shop Code</th>
                      <th>Market Name</th>
                      <th>Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {itemResults.map(item => (
                      <tr key={item.shop_id}>
                        <td style={{ fontFamily: 'DM Mono, monospace', fontWeight: 600 }}>{item.shop_code}</td>
                        <td>{item.market_name || '—'}</td>
                        <td style={{ textAlign: 'center', fontWeight: 600 }}>{item.item_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </>
      )}

      {/* ── RENT TAB ── */}
      {activeTab === 'rent' && (
        <RentTab shops={shops} />
      )}

      {/* ── ISSUE BONDS TAB ── */}
      {activeTab === 'bonds' && (
        <IssuesBondsTab shops={shops} />
      )}

      {/* ── Shop detail modal ── */}
      {selectedShop && (
        <ShopDetailModal
          shop={selectedShop}
          onClose={() => setSelected(null)}
          onEdit={shop => {
            setSelected(null);
            onEditShop(shop);
          }}
        />
      )}
    </div>
  );
}